import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";

// @ts-ignore - untyped JS module from coupon-common
import { actions as affiliateActions } from "coupon-common/src/modules/affiliateBusiness";

import { Footer } from "../../components/Footer";
import { HeaderWrapper } from "../../components/Header/HeaderWrapper";
import { FoundingPartnerSection, FreeDemoSection, PricingTier } from "../../components/LaunchPackage";
import { FAQSection } from "../../components/Page1/FAQSection";
import { BUSINESS_FAQS } from "../../components/Page2/businessFaqs";
import { BusinessHeroSection } from "../../components/Page2/BusinessHeroSection";
import { BusinessProfileSection } from "../../components/Page2/BusinessProfileSection";
import { CommunityRewardsSection } from "../../components/Page2/CommunityRewardsSection";
import { CustomerStatsSection } from "../../components/Page2/CustomerStatsSection";
import { DealIdeasSection } from "../../components/Page2/DealIdeasSection";
import { OurAdvantageSection } from "../../components/Page2/OurAdvantageSection";
import { TrustedBrandsSection } from "../../components/Page2/TrustedBrandsSection";
import { WhyCouponsWorkSection } from "../../components/Page2/WhyCouponsWorkSection";

type BrandLogo = { name: string; logo: string };

const SIGNUP_URL = "https://mycouponbook.ca/signup/business";

// Business membership plans shown in the "Premium Plan" pricing section.
const BUSINESS_TIERS: PricingTier<"starter" | "growth">[] = [
  {
    value: "starter",
    title: "Starter",
    price: "Free",
    valueText: "Access to valuable business add-ons",
    items: [
      "Up to 3 active coupons",
      "Basic analytics dashboard",
      "Email support",
      "Standard redemption processing",
    ],
    variant: "standard",
    buttonText: "Start for Free",
  },
  {
    value: "growth",
    title: "Growth",
    price: "$65",
    period: "/ month",
    valueText: "Get up to 20% points back on add-on purchases",
    items: [
      "20 active coupons",
      "Build for unlimited growth",
      "AI-Powered image generation",
      "Priority support & onboarding",
      "Advanced analysts & insights",
    ],
    variant: "featured",
    badgeText: "Most Popular",
    buttonText: "Start Free Trial",
  },
];

// Top 10 online (affiliate) brands shown in the carousel, in display order.
// `id` is the affiliate business id, used to pull the current logo from the API;
// `logo` is the fallback until that list loads (or if a brand is missing from it).
const TOP_ONLINE_BRANDS: (BrandLogo & { id: number })[] = [
  { id: 1, name: "Dyson", logo: "https://media-eu.jobylon.com/CACHE/companies/company-logo/dyson-operations-pte-ltd/gene_dyson_logo_white.8eec4069/7a86d3081f78fc804e27ab4d19e8f5bc.jpg" },
  { id: 11, name: "Well.ca", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSaNAoBBsrhpT3zwJyNgkYC7OUjIh9rOH0F-A&s" },
  { id: 17, name: "Brother", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcT2lSoSH59lt6AySXbMVzqyvrMOvYynqGAJzQ&s" },
  { id: 13, name: "Aosom", logo: "https://play-lh.googleusercontent.com/Mj8PPutPLKTeJv6BrjtEXBpFL9GCD57xAX2weE2TJNXs8yLe4rvrWY6zC2bf7rE4kSayVD1_H-JGq3z4v6kjJA=w240-h480-rw" },
  { id: 27, name: "Samsung", logo: "https://i.pinimg.com/736x/04/57/86/0457864646c26fa060f7ec3f6b5d7c05.jpg" },
  { id: 26, name: "Expedia", logo: "https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcSXkaXrSfebw8ANU9BW7IkLLqVDfFO8b8CE1Zu6SIK2vw&s=10" },
  { id: 34, name: "Under Armour", logo: "https://ui.awin.com/images/upload/merchant/profile/15432.png" },
  { id: 54, name: "Emma Sleep", logo: "https://ui.awin.com/images/upload/merchant/profile/106855.png" },
  { id: 28, name: "Temu", logo: "https://aimg.kwcdn.com/upload_aimg/m-img/785681a9-3a72-4e62-90e8-c9b8245739df.jpeg" },
  { id: 89, name: "CityPASS", logo: "https://play-lh.googleusercontent.com/4RKaiIeUwZcUDsejm5yykcAuPkXMXkQV0jq7ypJoEFaeWmjISF15r9Vd0HMEPIqdDO06JFv5E7GAQ0yUNB44pJ8" },
];

const Page2: React.FC = () => {
  const dispatch = useDispatch();

  // Online brands = affiliate businesses (same redux module Page1 reads from).
  const affiliateState = useSelector((s: any) => s.affiliateBusiness || {}, shallowEqual);
  const affiliateBusinesses = (affiliateState.data && affiliateState.data.results) || [];

  useEffect(() => {
    if (!affiliateState.fetching && !affiliateState.fetched) {
      dispatch(affiliateActions.getAffiliateBusinesses());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  // Keep the curated order and names; prefer the live logo when the API has one.
  const topBrands = useMemo<BrandLogo[]>(() => {
    const logosById = new Map<number, string>(
      affiliateBusinesses.filter((b: any) => b && b.logo).map((b: any) => [b.id, b.logo])
    );
    return TOP_ONLINE_BRANDS.map(({ id, name, logo }) => ({ name, logo: logosById.get(id) || logo }));
  }, [affiliateBusinesses]);

  return (
    <div className="business-page relative w-full bg-white overflow-x-hidden min-h-screen font-['Poppins',Helvetica,sans-serif]">
      {/* Neutralise Bootstrap reboot defaults (link underline, p/ul/heading margins) without beating Tailwind classes. */}
      <style>{`
        .business-page main :where(h1, h2, h3, h4, p, ul) { margin-bottom: 0; }
        .business-page main :where(ul) { padding-left: 0; list-style: none; }
        .business-page main :where(a) { text-decoration: none; }
      `}</style>
      <HeaderWrapper />

      <main className="relative z-10 flex flex-col items-center">
        <section id="hero" className="w-full">
          <BusinessHeroSection />
        </section>

        <section id="stats" className="w-full">
          <CustomerStatsSection />
        </section>

        <section id="profile" className="w-full">
          <BusinessProfileSection />
        </section>

        <section id="why-coupons" className="w-full">
          <WhyCouponsWorkSection />
        </section>

        <section id="brands" className="w-full">
          <TrustedBrandsSection brands={topBrands} />
        </section>

        <section id="deal-ideas" className="w-full">
          <DealIdeasSection />
        </section>

        <section id="advantage" className="w-full">
          <OurAdvantageSection />
        </section>

        <section id="community" className="w-full">
          <CommunityRewardsSection />
        </section>

        <section id="founding-partner" className="w-full">
          <FoundingPartnerSection
            tiers={BUSINESS_TIERS}
            label="Premium Plan"
            title={
              <>
                Ready to Scale? <span className="text-[#E00000] font-bold">Go Premium.</span>
              </>
            }
            subtitle="Unlock powerful tools designed for businesses that want to maximize their reach and revenue."
            footnote={null}
            onApply={() => {
              window.location.href = SIGNUP_URL;
            }}
          />
        </section>

        <section id="free-demo" className="w-full">
          <FreeDemoSection />
        </section>

        <section id="faq" className="w-full px-[16px] sm:px-6 lg:px-[80px] pt-16 lg:pt-[120px] pb-16 lg:pb-[140px]">
          <FAQSection items={BUSINESS_FAQS} containerClassName="w-full max-w-[1620px]" />
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Page2;
